// ─── Recherche d'adresse / lieu (BAN + Photon/OSM) ────────────────
// Brique PURE et réutilisable de la suite (fetch uniquement, aucune dépendance
// React/Firebase). Sert à la page Lieux, aux événements, aux contacts…
// Renvoie des items normalisés au format « Nominatim ».
//   - rechercherLieu(texte, villeBias) → [items]  (autocomplétion adresse/POI)
//   - rechercherVille(texte)           → [{nom, cp, label}]
//   - adresseDepuisCoords(lat, lng)    → 'adresse' (reverse, marche sur le web)
//   - formatAdresseNominatim / nomPrincipalLieu / adresseCourte / typeIconLieu

const UA = { 'User-Agent': 'MoP-Suite/1.0 contact@mopsuite.fr' };

// Photon (photon.komoot.io) : POI + adresses, tolérant aux fautes.
function photonVersNominatim(feature) {
  const p   = feature.properties || {};
  const geo = feature.geometry?.coordinates || [0, 0]; // [lon, lat]
  const rue = p.street || '';
  const num = p.housenumber || '';
  const nomAffiche = p.name || (num && rue ? `${num} ${rue}` : rue) || '';
  const parties = [
    nomAffiche,
    p.postcode && p.city ? `${p.postcode} ${p.city}` : (p.city || p.county || ''),
    p.country,
  ].filter(Boolean);
  return {
    place_id:     String(p.osm_id || ''),
    lat:          String(geo[1]),
    lon:          String(geo[0]),
    name:         nomAffiche,
    display_name: parties.join(', '),
    class:        p.osm_key   || 'place',
    type:         p.osm_value || p.type || '',
    address: {
      road:         rue,
      house_number: num,
      postcode:     p.postcode || '',
      city:         p.city || p.county || '',
      country:      p.country || '',
    },
  };
}

// BAN (api-adresse.data.gouv.fr) : numéros de rue précis pour la France.
function banVersNominatim(feature) {
  const p   = feature.properties || {};
  const geo = feature.geometry?.coordinates || [0, 0];
  const rue = p.street || p.name || '';
  const num = p.housenumber || '';
  const nomAffiche = num && rue ? `${num} ${rue}` : rue;
  return {
    place_id:     String(p.id || ''),
    lat:          String(geo[1]),
    lon:          String(geo[0]),
    name:         nomAffiche,
    display_name: p.label || nomAffiche,
    class:        'highway',
    type:         p.type === 'housenumber' ? 'residential' : 'road',
    address: {
      road:         rue,
      house_number: num,
      postcode:     p.postcode || '',
      city:         p.city || '',
      country:      'France',
    },
  };
}

// Déduplication par proximité géographique (~80 m).
function tropProche(a, b) {
  return (
    Math.abs(parseFloat(a.lat) - parseFloat(b.lat)) < 0.0008 &&
    Math.abs(parseFloat(a.lon) - parseFloat(b.lon)) < 0.0012
  );
}

// Nom principal du lieu (école, parc, médecin…).
export function nomPrincipalLieu(item) {
  if (item?.name) return item.name;
  return (item?.display_name || '').split(',')[0].trim();
}

// Adresse secondaire (rue + ville).
export function adresseCourte(item) {
  const a = item?.address || {};
  const parts = [];
  const numero = a.house_number || '';
  const rue    = a.road || a.pedestrian || a.path || a.street || '';
  if (rue) parts.push(numero ? `${numero} ${rue}` : rue);
  const ville = a.city || a.town || a.village || a.municipality || a.county || '';
  const cp    = a.postcode || '';
  if (ville) parts.push(cp ? `${cp} ${ville}` : ville);
  return parts.join(', ');
}

// Texte complet à stocker dans un champ « adresse » libre.
export function formatAdresseNominatim(item) {
  const nom     = nomPrincipalLieu(item);
  const adresse = adresseCourte(item);
  if (adresse) {
    // Évite « 7 Rue X, 7 Rue X, 91190 Ville » quand le nom EST déjà la rue.
    if (!nom || adresse.toLowerCase().startsWith(nom.toLowerCase())) return adresse;
    return `${nom}, ${adresse}`;
  }
  return (item?.display_name || '').split(',').slice(0, 3).join(',').trim();
}

// Icône selon la catégorie OSM.
export function typeIconLieu(item) {
  const cls  = item?.class || '';
  const type = item?.type  || '';
  if (cls === 'amenity') {
    if (['school', 'college', 'university', 'kindergarten'].includes(type)) return '🏫';
    if (['hospital', 'clinic', 'doctors', 'dentist'].includes(type))        return '🏥';
    if (type === 'pharmacy')      return '💊';
    if (type === 'library')       return '📚';
    if (type === 'post_office')   return '📮';
    if (['restaurant', 'cafe', 'fast_food', 'bar', 'pub'].includes(type))   return '🍽️';
    if (['bus_station', 'bus_stop'].includes(type)) return '🚌';
    if (type === 'place_of_worship')               return '⛪';
    if (['sports_centre', 'swimming_pool'].includes(type)) return '🏊';
  }
  if (cls === 'leisure' && ['park', 'garden', 'playground'].includes(type)) return '🌳';
  if (cls === 'shop')    return '🛒';
  if (cls === 'railway') return '🚉';
  if (cls === 'highway') return '🛣️';
  return '📍';
}

// Coordonnées → adresse (reverse geocoding). Fonctionne sur le WEB (contrairement
// à expo-location.reverseGeocodeAsync). BAN d'abord (France), repli Photon.
export async function adresseDepuisCoords(lat, lng) {
  if (lat == null || lng == null) return '';
  try {
    const rBAN = await fetch(`https://api-adresse.data.gouv.fr/reverse/?lon=${lng}&lat=${lat}`, { headers: UA })
      .then(r => r.json()).catch(() => null);
    const fB = rBAN?.features?.[0];
    if (fB) return formatAdresseNominatim(banVersNominatim(fB));
  } catch { /* indispo */ }
  try {
    const rPh = await fetch(`https://photon.komoot.io/reverse?lon=${lng}&lat=${lat}&lang=fr`, { headers: UA })
      .then(r => r.json()).catch(() => null);
    const fP = rPh?.features?.[0];
    if (fP) return formatAdresseNominatim(photonVersNominatim(fP));
  } catch { /* indispo */ }
  return '';
}

// Recherche de VILLES uniquement (BAN, type municipality). → [{nom, cp, label}].
export async function rechercherVille(texte) {
  const q = (texte || '').trim();
  if (q.length < 2) return [];
  try {
    const r = await fetch(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(q)}&type=municipality&limit=7`, { headers: UA })
      .then(x => x.json());
    const vus = new Set();
    const out = [];
    for (const f of (r?.features || [])) {
      const p = f.properties || {};
      const nom = p.city || p.name || '';
      const cp  = p.postcode || '';
      const label = cp ? `${nom} (${cp})` : nom;
      if (nom && !vus.has(label)) { vus.add(label); out.push({ nom, cp, label }); }
    }
    return out.slice(0, 6);
  } catch { return []; }
}

// Adresse libre → { lat, lng }, ou null. Beaucoup de lieux ont été saisis à la
// main (ou importés) SANS coordonnées : sans ce repli, leur fiche n'affiche
// aucune carte alors que l'adresse est parfaitement localisable.
const cacheGeo = new Map();   // adresse → {lat,lng} | null (évite de rejouer l'appel)
export async function geocoderAdresse(adresse) {
  const cle = String(adresse || '').trim();
  if (!cle) return null;
  if (cacheGeo.has(cle)) return cacheGeo.get(cle);
  const res = await rechercherLieu(cle);
  const p = res[0];
  const lat = p ? parseFloat(p.lat) : NaN;
  const lng = p ? parseFloat(p.lon) : NaN;
  const out = (!Number.isNaN(lat) && !Number.isNaN(lng)) ? { lat, lng } : null;
  cacheGeo.set(cle, out);
  return out;
}

// Recherche : BAN + Photon en parallèle, fusionnés (BAN d'abord), dédupliqués.
// villeBias (optionnel) : ajoutée aux requêtes courtes/ambiguës pour cibler.
export async function rechercherLieu(texte, villeBias = '') {
  if (!texte || texte.trim().length < 2) return [];
  const mots = texte.trim().split(/\s+/);
  const dejaSitue = texte.includes(',') || mots.length >= 3;
  const requete = (!dejaSitue && villeBias) ? `${texte.trim()} ${villeBias.trim()}` : texte.trim();
  try {
    const [resBAN, resPhoton] = await Promise.allSettled([
      fetch(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(requete)}&limit=5`, { headers: UA })
        .then(r => r.json()).then(g => (g?.features || []).map(banVersNominatim)),
      fetch(`https://photon.komoot.io/api/?q=${encodeURIComponent(requete)}&limit=6&lang=fr`, { headers: UA })
        .then(r => r.json()).then(g => (g?.features || []).map(photonVersNominatim)),
    ]);
    const banItems    = resBAN.status    === 'fulfilled' ? resBAN.value    : [];
    const photonItems = resPhoton.status === 'fulfilled' ? resPhoton.value : [];
    const fusionnes = [...banItems];
    for (const p of photonItems) {
      if (!fusionnes.some(b => tropProche(b, p))) fusionnes.push(p);
    }
    return fusionnes.slice(0, 8);
  } catch {
    return [];
  }
}
